import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { F } from 'ts-toolbelt';

import { getDocumentName } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { FolderPage_OneFolder_QueryDocument } from '../../../pages/Folder/hooks/useFindOneFolderQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import type { FolderBusinessEntityStateFormValues } from '../../FolderBusinessEntityState/types/values.type';
import type { UseUpdateFolderBusinessEntityStateSubmit } from '../../FolderBusinessEntityState/types/use-submit.type';

import { useUpdateFolderStateMutation } from './useUpdateFolderStateMutation.hook';
import type { FolderStateForm_UpdateFolderState_Result } from './useUpdateFolderStateMutation.hook';

export const useUpdateFolderStateSubmitFactory = (
  folderId: Id,
): UseUpdateFolderBusinessEntityStateSubmit =>
  function useUpdateFolderStateSubmit({
    memos,
    onSuccess: handleSuccess,
  }: F.Parameters<UseUpdateFolderBusinessEntityStateSubmit>[0]) {
    const [formError, setFormError] = useState<readonly string[] | null>(null);

    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const { mutate: updateFolderStateMutation } =
      useUpdateFolderStateMutation();

    const handleSubmit = useCallback(
      (
        values: Readonly<FolderBusinessEntityStateFormValues>,
        {
          setSubmitting,
        }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
      ) => {
        setFormError(null);

        const onSuccess = (
          _: Readonly<FolderStateForm_UpdateFolderState_Result>,
        ) => {
          enqueueSnackbar('Le statut de votre dossier a bien été mis à jour.');

          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(FolderPage_OneFolder_QueryDocument),
              {
                id: folderId,
              },
            ],
          });

          handleSuccess?.();

          setSubmitting(false);
        };

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));

          setSubmitting(false);
        };

        updateFolderStateMutation(
          {
            folderId,
            ...values,
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onSuccess,
            onError,
          },
        );
      },
      [
        enqueueSnackbar,
        handleSuccess,
        memos,
        queryClient,
        updateFolderStateMutation,
      ],
    );

    return {
      formError,
      handleSubmit,
    };
  };
